import CrudApiClient from './crudapiclient';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import type { MetaQuery } from '@refinedev/core';
import { axiosInstance } from './axios';

type SortStrategyMeta = MetaQuery & {
  strategyId?: string | number;
  collectionId?: string | number;
};

type CollectionAssignmentPayload = {
  collectionId?: string | number;
};

type QueryFilters = Record<string, unknown>;

interface AdvancedFilterGroup {
  operator: 'AND' | 'OR';
  filters: Record<string, unknown>;
}

interface AdvancedFilters {
  groups: AdvancedFilterGroup[];
  groupOperator: 'AND' | 'OR';
}

interface SortStrategyBucket {
  id?: number;
  sortStrategyId?: number;
  name: string;
  description: string;
  bucketOrder: number;
  layerOrder?: number;
  queryFilters?: QueryFilters;
  queryExclusions?: QueryFilters;
  advancedFilters?: AdvancedFilters;
  sortBy: string;
  pullLimit?: number | null;
  selectionMode?: 'ALL' | 'TOP_N' | 'TOP_PERCENT';
  selectionValue?: number | null;
}

export interface SortStrategyLayerConfig {
  name?: string;
  layerOrder: number;
  repeat?: boolean;
  queryFilters?: QueryFilters;
  queryExclusions?: QueryFilters;
  advancedFilters?: AdvancedFilters;
  pullLimit?: number | null;
}

export interface SortStrategyData {
  id?: number;
  name: string;
  description: string;
  repeat: boolean;
  isActive: boolean;
  buckets: SortStrategyBucket[];
  layerConfigs?: SortStrategyLayerConfig[];
}

class SortStrategyApiClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/catalog/sort-strategy');
  }

  private requireStrategyId(meta?: SortStrategyMeta): string | number {
    if (meta?.strategyId !== undefined && meta.strategyId !== null) {
      return meta.strategyId;
    }

    throw new Error('strategyId is required');
  }

  private requireCollectionId(
    payload?: CollectionAssignmentPayload,
    meta?: SortStrategyMeta,
  ): string | number {
    if (payload?.collectionId !== undefined && payload.collectionId !== null) {
      return payload.collectionId;
    }

    if (meta?.collectionId !== undefined && meta.collectionId !== null) {
      return meta.collectionId;
    }

    throw new Error('collectionId is required');
  }

  async updateById<T = any>(
    id: string | number,
    data: Partial<T>,
    meta: MetaQuery | undefined,
  ): Promise<T> {
    const url = this.resolveParameterizedUrlVariables(meta);
    const response = await axiosInstance.put(`${url}/${id}`, data, this.getRequestConfig(meta));
    return response.data;
  }

  async deleteById<T = any>(id: string | number, meta: MetaQuery | undefined): Promise<T> {
    const url = this.resolveParameterizedUrlVariables(meta);
    const response = await axiosInstance.delete(`${url}/${id}`, this.getRequestConfig(meta));
    return response.data;
  }

  async search<T = any>(payload: any, page = 1, size = 10, meta?: MetaQuery): Promise<Array<T>> {
    const url = this.resolveParameterizedUrlVariables(meta);
    const response = await axiosInstance.post(
      `${url}/search`,
      {
        ...payload,
        page,
        size,
      },
      this.getRequestConfig(meta),
    );
    return response.data.data || [];
  }

  async findBulk<T = any>(ids: Array<string | number>, meta?: MetaQuery): Promise<Array<T>> {
    if (!ids.length) {
      return [];
    }

    const url = this.resolveParameterizedUrlVariables(meta);
    const response = await axiosInstance.post(
      `${url}/search?page=1&size=${ids.length}`,
      {
        filters: [
          {
            field: 'id',
            filterType: 'IN',
            value: ids.map(String).join(','),
          },
        ],
      },
      this.getRequestConfig(meta),
    );

    return response.data?.data || [];
  }

  async getCollections(
    _sorters?: unknown,
    _filters?: unknown,
    _payload?: unknown,
    _query?: unknown,
    _headers?: unknown,
    meta?: SortStrategyMeta,
  ): Promise<number[]> {
    const strategyId = this.requireStrategyId(meta);
    const response = await axiosInstance.get(
      `${this.baseURL}/${strategyId}/collections`,
      this.getRequestConfig(meta),
    );
    return response.data.data || [];
  }

  async addCollection(
    _sorters?: unknown,
    _filters?: unknown,
    payload?: CollectionAssignmentPayload,
    _query?: unknown,
    _headers?: unknown,
    meta?: SortStrategyMeta,
  ): Promise<any> {
    const strategyId = this.requireStrategyId(meta);
    const collectionId = this.requireCollectionId(payload);
    const response = await axiosInstance.post(
      `${this.baseURL}/${strategyId}/collections/${collectionId}`,
      {},
      this.getRequestConfig(meta),
    );
    return response.data;
  }

  async removeCollection(
    _sorters?: unknown,
    _filters?: unknown,
    _payload?: CollectionAssignmentPayload,
    _query?: unknown,
    _headers?: unknown,
    meta?: SortStrategyMeta,
  ): Promise<any> {
    const strategyId = this.requireStrategyId(meta);
    const collectionId = this.requireCollectionId(undefined, meta);
    const response = await axiosInstance.delete(
      `${this.baseURL}/${strategyId}/collections/${collectionId}`,
      this.getRequestConfig(meta),
    );
    return response.data;
  }

  async getFacets(): Promise<any> {
    const response = await axiosInstance.post(GATEWAY_API_URL + '/search/styles', {
      searchType: 'FREETEXT',
      text: '',
      selectedFacets: {},
      page: 1,
      size: 1,
    });
    return response.data.data.facets || [];
  }
}

export default SortStrategyApiClient;
